import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { AiOutlineLoading } from "react-icons/ai";
import { HiArrowLeft, HiCheckCircle } from "react-icons/hi";
import dosee_logo from "../../../assets/images/dosee.png";
import { useAdminContext } from "../../context/admin/context/AdminContext";

const VerifyOtp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyAccount, resendOtp } = useAdminContext();

  // --- FIX START: Get email from State OR URL Query Params ---
  // This allows links like /verify-otp?email=john@example.com to work
  const queryParams = new URLSearchParams(location.search);
  const email = location.state?.email || queryParams.get("email");
  // --- FIX END ---

  // State
  const [otp, setOtp] = useState(new Array(6).fill(""));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState(null);

  // Resend Timer State
  const [timeLeft, setTimeLeft] = useState(60);
  const [canResend, setCanResend] = useState(false);

  // Refs
  const inputRefs = useRef([]);

  // Redirect if no email (security check)
  useEffect(() => {
    if (!email) {
      // If no email found in state OR url, then we redirect
      navigate("/signup", { replace: true });
    }
  }, [email, navigate]);

  // Timer Logic
  useEffect(() => {
    if (timeLeft > 0) {
      const timerId = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timerId);
    } else {
      setCanResend(true);
    }
  }, [timeLeft]);

  // --- INPUT HANDLERS ---
  const handleChange = (element, index) => {
    if (isNaN(element.value)) return false;

    const newOtp = [...otp];
    newOtp[index] = element.value;
    setOtp(newOtp);

    if (element.value !== "" && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      if (otp[index] === "" && index > 0) {
        inputRefs.current[index - 1].focus();
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 6);
    if (!/^\d+$/.test(pastedData)) return;

    const newOtp = [...otp];
    pastedData.split("").forEach((char, i) => {
      if (i < 6) newOtp[i] = char;
    });
    setOtp(newOtp);

    const focusIndex = Math.min(pastedData.length, 5);
    inputRefs.current[focusIndex].focus();
  };

  // --- API ACTIONS ---
  const handleVerify = async (e) => {
    e.preventDefault();
    const code = otp.join("");
    if (code.length !== 6) {
      setError(true);
      setMessage("Please enter the full 6-digit code.");
      return;
    }

    setLoading(true);
    setError(false);
    setMessage(null);

    try {
      const response = await verifyAccount({ email, code });

      console.log("Verify Response:", response);

      if (response.status === 200) {
        setSuccess(true);
        setMessage("Verification successful! Redirecting...");
        setTimeout(() => {
          navigate("/login");
        }, 2000);
      } else {
        setError(true);
        setMessage(response.message || "Invalid code. Please try again.");
      }
    } catch (err) {
      setError(true);
      setMessage("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendClick = async () => {
    if (!canResend) return;

    setLoading(true);
    setMessage(null);
    setError(false);

    try {
      const response = await resendOtp({ email });
      if (response.status === 200) {
        setSuccess(true);
        setMessage("A new code has been sent to your email.");
        setTimeLeft(60);
        setCanResend(false);
      } else {
        setError(true);
        setMessage(response.message || "Failed to resend code.");
      }
    } catch (err) {
      setError(true);
      setMessage("Could not resend code.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full overflow-y-auto bg-gray-50 text-gray-900 flex items-center justify-center p-4">
      <div
        className="fixed inset-0 bg-[radial-gradient(1200px_600px_at_50%_0%,rgba(16,185,129,0.05),transparent_60%)] pointer-events-none"
        aria-hidden="true"
      />

      <div className="relative w-full max-w-md">
        <div
          className="absolute -inset-2 rounded-3xl bg-gradient-to-tr from-emerald-200/50 via-amber-200/50 to-purple-200/50 blur-xl"
          aria-hidden="true"
        />

        <div className="relative rounded-3xl border border-gray-200 bg-white p-8 shadow-xl">
          <div className="mb-6">
            <Link
              to="/signup"
              className="flex items-center text-xs font-medium text-gray-500 hover:text-gray-900 transition-colors"
            >
              <HiArrowLeft className="mr-1 h-3 w-3" /> Back to Signup
            </Link>
          </div>

          <div className="text-center mb-8">
            <img
              src={dosee_logo}
              alt="Dosee Africa"
              className="h-10 w-auto mx-auto mb-4"
            />
            <h1 className="text-2xl font-bold tracking-tight text-gray-900">
              Verify Account
            </h1>
            <p className="mt-2 text-sm text-gray-600">
              Enter the 6-digit code sent to <br />
              {/* Added a fallback check for displaying the email */}
              <span className="font-semibold text-gray-900">
                {email || "your email address"}
              </span>
            </p>
          </div>

          <form onSubmit={handleVerify}>
            <div className="flex justify-between gap-2 mb-8">
              {otp.map((data, index) => (
                <input
                  key={index}
                  type="text"
                  name="otp"
                  maxLength="1"
                  ref={(el) => (inputRefs.current[index] = el)}
                  value={data}
                  onChange={(e) => handleChange(e.target, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  onPaste={index === 0 ? handlePaste : null}
                  onFocus={(e) => e.target.select()}
                  className={`
                    w-12 h-14 text-center text-xl font-bold rounded-xl border-2 outline-none transition-all
                    focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100
                    ${error ? "border-red-300 bg-red-50 text-red-900" : "border-gray-200 bg-white text-gray-900"}
                    ${data !== "" ? "border-emerald-500 bg-emerald-50" : ""}
                  `}
                />
              ))}
            </div>

            {message && (
              <div
                className={`mb-6 p-3 rounded-lg flex items-start gap-2 text-sm ${success ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-800"}`}
              >
                {success ? <HiCheckCircle className="h-5 w-5 mt-0.5" /> : null}
                <span>{message}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || otp.join("").length !== 6}
              className="group relative inline-flex w-full items-center justify-center overflow-hidden rounded-xl bg-gray-900 px-4 py-3 text-sm font-semibold text-white shadow-lg transition hover:shadow-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              <span className="absolute inset-0 -z-10 bg-gradient-to-r from-emerald-500 via-amber-500 to-purple-500 opacity-0 transition group-hover:opacity-100" />
              {loading ? (
                <AiOutlineLoading className="text-lg animate-spin" />
              ) : (
                <span>Verify & Proceed</span>
              )}
            </button>
          </form>

          <div className="mt-6 text-center text-xs text-gray-500">
            Didn't receive code?{" "}
            {canResend ? (
              <button
                onClick={handleResendClick}
                className="font-bold text-emerald-600 hover:text-emerald-500 hover:underline"
              >
                Resend Code
              </button>
            ) : (
              <span className="text-gray-400">
                Resend in <span className="font-mono">{timeLeft}s</span>
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyOtp;
